// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

import { css } from '@emotion/css';
import { TextField, InputAdornment, Icon, IconButton, MenuItem, Divider, Theme, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import ReusableMultiselect from '@components/Selectors/ReusableMultiselect';
import Spacer from '@components/Utils/Spacer';

// COMPONENT ---------------------------------------------------------------- //

interface MissingRefReportControlsProps {
  range: string;
  start: string;
  end: string;
  disablePickers: boolean;
  usernames: string[];
  search: string;
  setUsernames: (usernames: string[]) => void;
  setSearch: (search: string) => void;
  onRangeChange: (range: string) => void;
  onDateChange: (type: 'start' | 'end') => (date: string) => void;
  usernamesPool: string[];
}

const MissingRefReportControls = ({
  range,
  start,
  end,
  disablePickers,
  usernames,
  search,
  setUsernames,
  setSearch,
  onRangeChange,
  onDateChange,
  usernamesPool,
}: MissingRefReportControlsProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  // Local search input state (updates on every keystroke)
  const [searchInput, setSearchInput] = React.useState<string>(search || '');

  // Sync local input with context search when context changes
  React.useEffect(() => {
    setSearchInput(search || '');
  }, [search]);

  // Handle search commit (Enter key or button click)
  const handleSearchCommit = () => {
    setSearch(searchInput.trim());
  };

  // Handle Enter key press in search field
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSearchCommit();
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const handleUsernamesChange = (
    _: string | null,
    arr?: {
      id: string | null | undefined;
      name: string | null | undefined;
    }[]
  ) => {
    setUsernames?.(arr?.map(item => item.id || '') || []);
  };

  return (
    <>
      <div className={cls.controls}>
        <TextField
          select
          fullWidth
          label='Date Range'
          placeholder='Select a date range...'
          variant='outlined'
          size='small'
          value={range}
          onChange={e => onRangeChange?.(e.target.value)}
        >
          <MenuItem value='tomorrow'>
            <span className={cls.rangeText}>Tomorrow</span>
            <span className={cls.rangeSubText}>{dayjs().add(1, 'day').format('MM/DD/YYYY')}</span>
          </MenuItem>
          <MenuItem value='today'>
            <span className={cls.rangeText}>Today</span>
            <span className={cls.rangeSubText}>{dayjs().format('MM/DD/YYYY')}</span>
          </MenuItem>
          <MenuItem value='yesterday'>
            <span className={cls.rangeText}>Yesterday</span>
            <span className={cls.rangeSubText}>{dayjs().subtract(1, 'day').format('MM/DD/YYYY')}</span>
          </MenuItem>

          <Divider />

          <MenuItem value='nextweek'>
            <span className={cls.rangeText}>Next Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().add(1, 'week').startOf('week').format('MM/DD/YYYY')} -{' '}
              {dayjs().add(1, 'week').endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='thisweek'>
            <span className={cls.rangeText}>This Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().startOf('week').format('MM/DD/YYYY')} - {dayjs().endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='lastweek'>
            <span className={cls.rangeText}>Last Week</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'week').startOf('week').format('MM/DD/YYYY')} -{' '}
              {dayjs().subtract(1, 'week').endOf('week').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='nextmonth'>
            <span className={cls.rangeText}>Next Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().add(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
              {dayjs().add(1, 'month').endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='thismonth'>
            <span className={cls.rangeText}>This Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().startOf('month').format('MM/DD/YYYY')} - {dayjs().endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>
          <MenuItem value='lastmonth'>
            <span className={cls.rangeText}>Last Month</span>
            <span className={cls.rangeSubText}>
              {dayjs().subtract(1, 'month').startOf('month').format('MM/DD/YYYY')} -{' '}
              {dayjs().subtract(1, 'month').endOf('month').format('MM/DD/YYYY')}
            </span>
          </MenuItem>

          <Divider />

          <MenuItem value='custom'>Custom</MenuItem>
        </TextField>

        <DatePicker
          disabled={disablePickers}
          maxDate={dayjs(end)}
          label='Start Date'
          orientation='portrait'
          value={dayjs(start)}
          onChange={date => onDateChange?.('start')(date?.toString() || '')}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
            },
          }}
        />

        <DatePicker
          disabled={disablePickers}
          minDate={dayjs(start)}
          label='End Date'
          orientation='portrait'
          value={dayjs(end)}
          onChange={date => onDateChange?.('end')(date?.toString() || '')}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              variant: 'outlined',
            },
          }}
        />
      </div>

      <Spacer size={1.5} />

      <div className={cls.controls}>
        <ReusableMultiselect
          label='Filter By User'
          dataArray={usernamesPool?.map(item => ({ id: item, name: item || 'N/A' })) || []}
          selected={usernames?.map(item => ({ id: item, name: item || 'N/A' })) || []}
          handleChange={handleUsernamesChange}
        />

        <TextField
          fullWidth
          label='Search (Press Enter)'
          placeholder='Search for moves...'
          variant='outlined'
          size='small'
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon color='disabled' fontSize='small'>
                    search
                  </Icon>
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position='end'>
                  {searchInput !== search ? (
                    <IconButton size='small' onClick={handleSearchCommit} title='Search'>
                      <Icon fontSize='small'>arrow_forward</Icon>
                    </IconButton>
                  ) : (
                    <IconButton size='small' onClick={handleClearSearch} title='Clear search'>
                      <Icon fontSize='small'>close</Icon>
                    </IconButton>
                  )}
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </div>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  controls: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  `,

  rangeText: css`
    margin-right: 6px;
  `,
  rangeSubText: css`
    font-size: 12px;
    color: ${theme.palette.text.disabled};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MissingRefReportControls;
